import PropTypes from 'prop-types';
import React from 'react';

import RateGroup from '../../models/rateGroup';
import Event from '../../models/eventModel';
import { Input, Icon, TooltipHelp, Section, Modal, Button, Form, Select, SelectHierarchy } from '@weezevent/nacre';
import SessionModel from '../../models/sessionModel';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../components/utils/useDateFormat';
import { LoadingOptions } from '../../components/loadingCard';

function SubscriptionLinkModal({ organizationId, subscriptionLink, onSuccess, onClose, onError }) {
    const { t } = useTranslation();

    const [state, setState] = React.useState({
        eventId: null,
        rateGroups: [],
        sessionsGroups: null,
        openTarifGroupe: false,
        use_sessions: false
    });

    const [searchQuery, setSearchQuery] = React.useState('');
    const searchDebounceRef = React.useRef(null);

    const [events, loadingEvents] = Event.useApiModel(
        {
            organizationId,
            query: {
                publication_status__in: 'published,draft',
                time_status: 'upcoming,ongoing',
                limit: 50,
                search: searchQuery
            }
        },
        [organizationId, searchQuery]
    );

    let setEvent = eventId => {
        setState({
            ...state,
            rateGroups: [],
            sessionsGroups: null
        });

        RateGroup.getAll({ organizationId, eventId }).then(rateGroups => {
            const use_sessions = events.find(e => e.pk === eventId)?.use_sessions;
            if (use_sessions) {
                SessionModel.getAll({
                    organizationId: organizationId,
                    eventId: eventId,
                    query: {
                        limit: 9999
                    }
                }).then(sessionsGroups => {
                    setState({
                        ...state,
                        eventId,
                        rateGroups,
                        sessionsGroups,
                        use_sessions: use_sessions
                    });
                });
            } else {
                setState({
                    ...state,
                    eventId,
                    rateGroups,
                    use_sessions: use_sessions
                });
            }
        });
    };

    let renderRateGroupSelector = () => {
        return (
            <Form.Field>
                <label>{t('ticket.webservice.rateGroupsSelection')}</label>
                <Select
                    search
                    clearable
                    disabled={Boolean(!state.eventId || !state.rateGroups) || (Array.isArray(state.rateGroups) && !state.rateGroups.length)}
                    placeholder={Array.isArray(state.rateGroups) && !state.rateGroups.length ? '-' : ''}
                    options={state.rateGroups && state.rateGroups?.map(rateGroup => ({ text: `${rateGroup.name} (${rateGroup.pk})`, value: rateGroup.pk }))}
                    onChange={(synth, { value }) => setState({ ...state, rateGroup: value })}
                />
            </Form.Field>
        );
    };

    let renderSessionSelector = () => {
        return (
            <Form.Field>
                <label>{t('ticket.webservice.linkModal.selectSessionTitle')}</label>
                <Select
                    search
                    clearable
                    disabled={Boolean(!state.eventId || !state.sessionsGroups) || (Array.isArray(state.sessionsGroups) && !state.sessionsGroups.length)}
                    placeholder={t('ticket.webservice.linkModal.selectSessionPlacholder')}
                    options={
                        state.sessionsGroups &&
                        state.sessionsGroups?.map(sessionsGroup => {
                            const event = events.find(ev => ev.pk === state.eventId);
                            const [properDate, properTime] = useDateFormat({
                                date: sessionsGroup.start_date,
                                event: event,
                                dateStyle: 'short',
                                timeStyle: 'short'
                            });
                            return { text: `${sessionsGroup.name} ${properDate} - ${properTime}`, value: sessionsGroup.weez_date_id };
                        })
                    }
                    onChange={(synth, { value }) => setState({ ...state, sessionsGroup: value })}
                />
            </Form.Field>
        );
    };

    let link = () => {
        subscriptionLink
            .link({
                subscriptionId: subscriptionLink.subscription,
                organizationId,
                eventId: state.eventId,
                rateGroupId: state.rateGroup,
                date_id: state.sessionsGroup
            })
            .then(e => {
                onSuccess(e);
                onClose();
            })
            .catch(e => {
                onError();
            })
            .finally(() => {
                onClose();
            });
    };

    const getEventsOptions = React.useCallback(() => {
        if (loadingEvents) {
            return LoadingOptions;
        }
        return (events || []).map(event => ({
            text: event.name,
            use_sessions: event.use_sessions,
            value: event.pk,
            key: event.pk
        }));
    }, [events, loadingEvents]);

    const handleExternalSearch = React.useCallback(q => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setSearchQuery(q || '');
        }, 300);
    }, []);

    if (!subscriptionLink) {
        return null;
    }

    return (
        <Modal
            open={Boolean(subscriptionLink)}
            title={t('ticket.webservice.list.link')}
            size="large"
            submitButton={
                <Button
                    disabled={!state.eventId}
                    primary
                    label={t('ticket.common.action.save')}
                    onClick={() => {
                        link();
                        setState({
                            ...state,
                            eventId: null,
                            rateGroup: null,
                            rateGroups: [],
                            sessionsGroup: null
                        });
                        setSearchQuery('');
                    }}
                />
            }
            onClose={() => {
                setState({
                    ...state,
                    eventId: null,
                    rateGroup: null,
                    rateGroups: [],
                    sessionsGroup: null
                });
                setSearchQuery('');
                onClose();
            }}
            cancelButton={
                <Button
                    label={t('ticket.common.action.cancel')}
                    onClick={() => {
                        setState({
                            ...state,
                            eventId: null,
                            rateGroup: null,
                            rateGroups: [],
                            sessionsGroup: null
                        });
                        setSearchQuery('');
                        onClose();
                    }}
                />
            }
        >
            <Form>
                <Form.Field>
                    <label>{t('ticket.webservice.linkModal.linkDistributorEvent')}</label>
                    <Input disabled value={subscriptionLink.name} onChange={(synth, { value }) => setEvent(value)} />
                </Form.Field>
                <Form.Field>
                    <label>{t('ticket.webservice.linkModal.linkWeezEvent')}</label>
                    <SelectHierarchy
                        placeholder={t('ticket.common.action.select')}
                        searchable
                        onExternalSearch={handleExternalSearch}
                        externalOptions={getEventsOptions()}
                        onChange={(optionValue, option) => setEvent(optionValue, option?.use_sessions)}
                    />
                </Form.Field>
                {state.use_sessions ? <Section>{renderSessionSelector()}</Section> : null}
                <div
                    style={{ display: 'flex', 'align-items': 'center', 'margin-bottom': '13px' }}
                    onClick={() => {
                        setState({
                            ...state,
                            openTarifGroupe: !state.openTarifGroupe
                        });
                    }}
                >
                    <Icon name={state.openTarifGroupe ? 'less' : 'plus'}></Icon>
                    <div style={{ 'font-weight': '500' }}>{t('ticket.webservice.linkModal.tarifGroupe')}</div>
                    <TooltipHelp title={t('ticket.webservice.linkModal.tooltipRateGroup')}></TooltipHelp>
                </div>
                {state.openTarifGroupe && state.rateGroups ? <Section>{renderRateGroupSelector()}</Section> : null}
            </Form>
        </Modal>
    );
}

SubscriptionLinkModal.defaultProps = {
    open: true,
    subscriptionLink: null
};

SubscriptionLinkModal.propTypes = {
    onError: PropTypes.func.isRequired,
    organizationId: PropTypes.string.isRequired,
    subscriptionLink: PropTypes.object,
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired
};

export default SubscriptionLinkModal;
